"use client";

import React, { useEffect, useState } from "react";
import { Leaf, Star, GraduationCap, Calendar } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface AnimatedCounterProps {
  end: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}
function AnimatedCounter({
  end,
  prefix = "",
  suffix = "",
  duration = 2000
}: AnimatedCounterProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number | null = null;
    let animationFrameId: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - percentage, 4);
      setCount(Math.floor(easeOutQuart * end));
      if (percentage < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isVisible, end, duration]);
  return <span ref={ref} className="text-6xl md:text-7xl lg:text-8xl font-mono font-bold tracking-tighter text-primary">
      {prefix}{count}{suffix}
    </span>;
}
const statsData = [{
  icon: Calendar,
  value: 17,
  suffix: "+",
  label: "Years Experience",
  description: "A career dedicated to mathematical excellence, from the classroom to personalized virtual sessions."
}, {
  icon: Star,
  value: 5,
  prefix: "",
  suffix: ".0",
  label: "5-Star Rating",
  description: "Uncompromising quality in instruction, reflected in every student review and success story."
}, {
  icon: GraduationCap,
  value: 2,
  prefix: "",
  suffix: "",
  label: "State Credentials",
  description: "Certified in California and Arizona, plus National Board Certification for elite teaching standards."
}];
export default function SustainableSourcing() {
  const {
    ref: narrativeRef,
    isVisible: isNarrativeVisible
  } = useRevealOnScroll({
    once: true
  });
  return <section data-section-id="223882" id="the-berkeley-standard" className="bg-muted text-foreground py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        
        {}
        <div ref={narrativeRef} className={`max-w-3xl mx-auto text-center transition-all duration-1000 transform ${isNarrativeVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-sm font-default font-semibold tracking-widest uppercase text-primary mb-4">The COTTONE Standard</h2>
          <h3 className="font-serif mb-6 leading-tight !font-bold !text-[40px]">
            Academic Rigor Meets Personalized Support
          </h3>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">Leveraging a BA in Math from UC Berkeley and an MA in Teaching Mathematics,&nbsp;<br />Mr. Cottone provides a sophisticated, empathetic approach to tutoring that builds both technical mastery and joy of learning in every student.</p>
        </div>

        {}
        <div className="flex justify-center my-16 md:my-24">
          <Separator className="w-24 bg-secondary h-[2px]" />
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 lg:gap-16">
          {statsData.map((stat, i) => <div key={i} data-index={i} className="flex flex-col items-center text-center group bg-card p-8 rounded-lg border-t-4 border-secondary shadow-md transition-all duration-500 hover:-translate-y-[5px] hover:shadow-lg">
              <div className="mb-8 p-4 rounded-lg bg-muted text-primary transition-transform duration-500 group-hover:scale-110">
                <stat.icon size={40} strokeWidth={1.5} />
              </div>
              
              <div className="mb-4 h-24 flex items-end justify-center">
                <AnimatedCounter end={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
              </div>
              
              <h4 className="text-xl font-default font-semibold mb-3 text-primary">
                {stat.label}
              </h4>
              
              <p className="text-muted-foreground font-default text-sm md:text-base leading-relaxed max-w-[280px]">
                {stat.description}
              </p>
            </div>)}
        </div>

      </div>
    </section>;
}